import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, InputBase, Popover, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { LuSend } from 'react-icons/lu';
import DosenTooltip from '../ui/DosenTooltip';
import ElliottFace from '../../pages/new-designs/components/ElliottFace';
import HeaderElliottSlot from '../layout/HeaderElliottSlot';
import { getUserDetails } from '../../utils/orgName';
import { elliottTokens as t } from '../../pages/new-designs/tabs/home/elliott-lesson/tokens';
import { ELLIOTT_CHAT_ROUTE } from './gamificationTypes';
import { isCapabilityRelease } from '../../utils/capabilityRelease';

// Elliott corner orb — learner counterpart of the admin Framework orb, per
// the design doc §1.8: "Elliott in the bottom right corner, on every page,
// always one click away." Click opens a small panel with prompts tuned to
// the page; every prompt maps to a capability Elliott has today.
// Concept: mounted on the pathway; production mounts it in the learner
// Layout so it appears on every learner page.
//
// The face is the shared ElliottFace (same character as the admin Framework
// orb — white eyes, blink, wander) so Elliott reads identically everywhere.

const SURFACE = '#1C1C1C';
const BORDER_DIM = '#2A2A2A';
const TEXT_PRIMARY = '#FDFDFD';
const TEXT_SECONDARY = '#B6B6B6';
const TEXT_TERTIARY = '#868686';
const BRAND_LIGHT = '#F79420';
const BRANDY_GRADIENT = 'linear-gradient(180deg, #F79420 0%, #F15A26 100%)';

// Paragraph wrap width. The panel sizes itself to the widest (nowrap) prompt,
// so the intro needs its own cap — without it a paragraph's max-content width
// would blow the panel out to a single enormous line. Sized so the greeting
// ("Hi, I'm Elliott, your personal Knowledge Assistant for all things …")
// always fits on the first line rather than spilling onto a second.
const COPY_WIDTH = 640;

// Face column in the panel header. The prompts and input below are inset by the
// same amount so everything under the welcome lines up with its text.
const FACE_SIZE = 30;
const FACE_GAP = 10;

// What Elliott is, when to reach for him, and what asking earns — behind the
// (i) beside the panel heading, so the panel itself stays a greeting and an
// input rather than an explainer.
const TOOLTIP_SECTIONS: { heading: string; body: string }[] = [
  {
    heading: 'WHAT ELLIOTT KNOWS',
    body: "Elliott combines leading AI models with your organization's own knowledge in Dosen. That means its answers are informed not just by general knowledge, but by the unique information your admins provide about your business."
  },
  {
    heading: 'WHEN TO ASK',
    body: 'Any time you have a question about your role, your organization, or your learning. You get an answer straight away, without digging through files or waiting for a reply.'
  },
  {
    heading: 'YOUR LEARNING TIER',
    // In the Activity names with the Capability release (utils/capabilityRelease.ts).
    get body() {
      return isCapabilityRelease()
        ? 'Every message you send Elliott counts toward Proactive Learning, one of the measures behind your Learning Tier. You reach Excellent by messaging Elliott in the last 7 days (High within 14 days, Moderate within 30). Exceptional needs 3 or more messages in the last 7 days.'
        : 'Every message you send Elliott counts toward Proactive Learning, one of the five measures behind your Learning Tier. Over 30 days, 4 messages gets you to Bronze, 8 reaches Silver and 12 reaches Gold.';
    }
  }
];

// Prompts the orb's bubble types out, one after another. The introduction leads
// every rotation, so the first thing a learner sees is who Elliott is.
const learnerSpeeches = (firstName: string): string[] => [
  firstName
    ? `Hi ${firstName}, I'm Elliott. Ask me anything about your learning.`
    : "Hi, I'm Elliott. Ask me anything about your learning.",
  'Give me a detailed overview of our customer accounts.',
  'What is our business development process?',
  "What is our company's focus for the next 5 years and how can I add value?"
];

const ElliottOrb: React.FC = () => {
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [draft, setDraft] = useState('');

  const firstName = (getUserDetails().firstName || '').trim();

  // Hand the question to Elliott: the chat page picks `elliottInitialMessage`
  // off the route state, opens a new conversation, sends it, and shows the
  // thinking indicator while the answer comes back.
  const ask = (question: string) => {
    const trimmed = question.trim();
    if (!trimmed) return;
    setAnchor(null);
    setDraft('');
    navigate(ELLIOTT_CHAT_ROUTE, { state: { elliottInitialMessage: trimmed } });
  };

  // Typed-out prompts in a bubble beside the orb — same treatment as the admin
  // Framework orb. Each line types itself, holds, then the next replaces it.
  const [speechIndex, setSpeechIndex] = useState(0);
  const [typedCount, setTypedCount] = useState(0);
  const speeches = useMemo(() => learnerSpeeches(firstName), [firstName]);
  const speech = speeches[speechIndex];
  const count = speeches.length;
  const advance = useCallback(() => setSpeechIndex((i) => (i + 1) % count), [count]);
  useEffect(() => {
    setTypedCount(0);
    let hold: number | undefined;
    let typed = 0;
    const type = window.setInterval(() => {
      typed += 1;
      setTypedCount(typed);
      if (typed >= speech.length) {
        window.clearInterval(type);
        hold = window.setTimeout(advance, 3500);
      }
    }, 45);
    return () => {
      window.clearInterval(type);
      if (hold) window.clearTimeout(hold);
    };
  }, [speech, advance]);
  const typing = typedCount < speech.length;

  return (
    // Lives in the top bar, just left of the notifications bell.
    <HeaderElliottSlot>
      <Box
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', minWidth: 0, maxWidth: 560 }}
      >
        {/* Speech bubble (left of the orb). A phone shows the orb alone (mobile-app.css). */}
        <Box
          className="elliott-orb-bubble"
          sx={{
            position: 'relative',
            minWidth: 0,
            overflow: 'hidden',
            px: '12px',
            py: '8px',
            borderRadius: '10px',
            bgcolor: SURFACE,
            border: `1.5px solid ${BRAND_LIGHT}`,
            boxShadow: '0 0 10px rgba(247,148,32,0.25)'
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: 13,
              fontWeight: 500,
              fontStyle: 'italic',
              lineHeight: 1.4,
              color: TEXT_PRIMARY,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {speech.slice(0, typedCount)}
            {typing && (
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  width: 0,
                  transform: 'translateX(1px)',
                  color: BRAND_LIGHT,
                  animation: 'learnerOrbCaret 1s steps(1) infinite',
                  '@keyframes learnerOrbCaret': { '0%,50%': { opacity: 1 }, '50.01%,100%': { opacity: 0 } }
                }}
              >
                ▍
              </Box>
            )}
          </Typography>
        </Box>
        <Box
          aria-label="Ask Elliott"
          sx={{
            display: 'flex',
            cursor: 'pointer',
            borderRadius: '44%',
            boxShadow: '0 4px 14px rgba(241,90,38,0.35)',
            transition: 'transform 0.15s ease, box-shadow 0.15s ease',
            '&:hover': { transform: 'scale(1.06)', boxShadow: '0 6px 18px rgba(241,90,38,0.5)' }
          }}
        >
          <ElliottFace size={34} pulse />
        </Box>
      </Box>

      <Popover
        open={anchor !== null}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        // Opens downward from the header.
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            bgcolor: SURFACE,
            // Brandy edge + glow so the panel belongs to Elliott, not the page.
            border: `1px solid rgba(247,148,32,0.45)`,
            borderRadius: '12px',
            p: '14px 16px 16px',
            // Wide enough for a real question to be typed and read back before
            // sending; the field wraps to a second line rather than the panel
            // growing sideways. Capped so it can't run off a narrow viewport.
            width: 520,
            maxWidth: 'calc(100vw - 48px)',
            mt: '10px',
            overflow: 'hidden',
            boxShadow: '0 12px 32px rgba(0,0,0,0.55), 0 0 18px rgba(247,148,32,0.18)'
          }
        }}
      >
        {/* Brandy accent bar across the top of the panel. */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            background: BRANDY_GRADIENT
          }}
        />

        {/* Welcome — the lesson page's treatment: heading, greeting, and the
            same brandy input bar, with nothing between them. */}
        <Box sx={{ mb: '10px' }}>
          <Box>
            {/* Heading + (i): what Elliott is lives in the tooltip, not here. */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', mb: '5px' }}>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '1.2px',
                  color: BRAND_LIGHT,
                  lineHeight: 1
                }}
              >
                ELLIOTT
              </Typography>
              <DosenTooltip
                tapOnPhone
                placement="bottom-start"
                componentsProps={{ tooltip: { sx: { maxWidth: 380 } } }}
                title={
                  <Box>
                    {TOOLTIP_SECTIONS.map((section, i) => (
                      <Box key={section.heading} sx={{ mt: i === 0 ? 0 : '10px' }}>
                        <Typography
                          sx={{
                            fontFamily: 'Inter, sans-serif',
                            fontSize: 10,
                            fontWeight: 700,
                            letterSpacing: '1px',
                            color: BRAND_LIGHT,
                            mb: '3px'
                          }}
                        >
                          {section.heading}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: 'Inter, sans-serif',
                            fontSize: 11.5,
                            lineHeight: 1.5,
                            color: TEXT_SECONDARY
                          }}
                        >
                          {section.body}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                }
              >
                <InfoOutlinedIcon sx={{ fontSize: 15, color: TEXT_SECONDARY, cursor: 'default', display: 'block' }} />
              </DosenTooltip>
            </Box>
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 12.5,
                fontWeight: 700,
                lineHeight: 1.55,
                color: TEXT_PRIMARY
              }}
            >
              {firstName ? `Hi ${firstName}, how can I help?` : 'Hi, how can I help?'}
            </Typography>
          </Box>
        </Box>

        {/* The lesson page's input bar, verbatim in treatment: brandy 1.5px
            edge, orange glow, transparent field, LuSend that lights up once
            there's something to send. */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            px: '12px',
            py: '8px',
            borderRadius: `${t.radius}px`,
            bgcolor: t.surface,
            border: `1.5px solid ${t.brandLight}`,
            boxShadow: '0px 0px 16px 0px rgba(244,119,35,0.5), inset 0px 0px 8px 0px rgba(247,148,32,0.25)'
          }}
        >
          <InputBase
            multiline
            maxRows={6}
            value={draft}
            autoComplete="off"
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                ask(draft);
              }
            }}
            placeholder="Ask me anything"
            sx={{
              flex: 1,
              minWidth: 0,
              color: t.textPrimary,
              // A step down from the lesson bar's 16px: this panel is header
              // chrome, not a full-page conversation.
              fontSize: 14,
              fontWeight: 500,
              lineHeight: 1.45,
              '& textarea, & input': { backgroundColor: 'transparent', whiteSpace: 'pre-wrap' },
              '& textarea::placeholder, & input::placeholder': { color: t.textSecondary, opacity: 1 }
            }}
          />
          <Box
            onClick={() => ask(draft)}
            aria-label="Send to Elliott"
            sx={{
              display: 'flex',
              flexShrink: 0,
              color: draft.trim() ? t.brandLight : t.textTertiary,
              cursor: draft.trim() ? 'pointer' : 'default'
            }}
          >
            <LuSend size={18} />
          </Box>
        </Box>
      </Popover>
    </HeaderElliottSlot>
  );
};

export default ElliottOrb;
